/**
 * Construcción del contenido que se lleva el portapapeles al copiar un ticket.
 *
 * Se generan dos versiones y se escriben juntas: el destino elige la que
 * entienda. `text/plain` es lo que acaba en Slack, WhatsApp, Notas o un editor
 * de código; `text/html` es lo que pegan Gmail, Word, Docs u Outlook.
 *
 * Criterios de formato:
 *  - Nada de arte ASCII ni tablas dibujadas con guiones: se rompen en cuanto
 *    cambia la fuente o el ancho del destino.
 *  - Bloques separados por una línea en blanco y listas con "- ": es lo que
 *    todos los clientes respetan sin convertirlo en otra cosa.
 *  - En el HTML, medidas en pt y colores neutros: Word y Outlook reescriben px
 *    y los fondos de color quedan sucios al pegar sobre documentos existentes.
 */

const GUION = '—';

const escaparHtml = (valor) => {
    if (valor === null || valor === undefined || valor === '') return '';
    return String(valor)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

const limpiar = (valor) => (valor === null || valor === undefined ? '' : String(valor).trim());

/** Fecha larga en es-ES, con hora en 24h y sin la coma de separación. */
export const fechaLarga = (valor) => {
    if (!valor) return '';
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) return '';
    return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(fecha).replace(',', ' a las');
};

/**
 * HTML del editor → texto plano legible: párrafos separados por línea en
 * blanco, listas con guion y enlaces como «texto (url)» para que la dirección
 * no se pierda al pegar en un sitio sin formato.
 */
export const htmlATextoPlano = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(`<body>${String(html)}</body>`, 'text/html');

    const recorrer = (nodo) => {
        if (nodo.nodeType === Node.TEXT_NODE) {
            return nodo.textContent.replace(/\s+/g, ' ');
        }
        if (nodo.nodeType !== Node.ELEMENT_NODE) return '';

        const hijos = () => Array.from(nodo.childNodes).map(recorrer).join('');

        switch (nodo.nodeName) {
            case 'BR':
                return '\n';
            case 'P':
            case 'DIV':
            case 'H1':
            case 'H2':
            case 'H3':
            case 'H4':
                return `${hijos().trim()}\n\n`;
            case 'BLOCKQUOTE':
                return `${hijos().trim().split('\n').map((l) => `> ${l}`).join('\n')}\n\n`;
            case 'UL':
            case 'OL':
                return `${Array.from(nodo.children).map((li, i) => {
                    const marca = nodo.nodeName === 'OL' ? `${i + 1}.` : '-';
                    return `${marca} ${recorrer(li).trim()}`;
                }).join('\n')}\n\n`;
            case 'LI':
                return hijos();
            case 'A': {
                const texto = hijos().trim();
                const href = nodo.getAttribute('href') || '';
                if (!href || href === texto) return texto || href;
                return `${texto} (${href})`;
            }
            default:
                return hijos();
        }
    };

    return Array.from(doc.body.childNodes)
        .map(recorrer)
        .join('')
        .replace(/ /g, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

/**
 * Descripción saneada para el HTML del portapapeles: solo etiquetas de texto,
 * sin estilos, clases ni scripts heredados de lo que se pegó en el editor.
 */
const sanearFragmento = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(`<body>${String(html)}</body>`, 'text/html');
    const permitidas = ['P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'A', 'BLOCKQUOTE', 'CODE', 'PRE'];

    const recorrer = (nodo) => {
        if (nodo.nodeType === Node.TEXT_NODE) return escaparHtml(nodo.textContent);
        if (nodo.nodeType !== Node.ELEMENT_NODE) return '';

        const interior = Array.from(nodo.childNodes).map(recorrer).join('');

        if (!permitidas.includes(nodo.nodeName)) return interior;

        if (nodo.nodeName === 'BR') return '<br />';

        if (nodo.nodeName === 'A') {
            const href = nodo.getAttribute('href') || '';
            // Solo esquemas de navegación/contacto: evita javascript: y data:
            const seguro = /^(https?:|mailto:|tel:)/i.test(href);
            if (!seguro) return interior;
            return `<a href="${escaparHtml(href)}">${interior || escaparHtml(href)}</a>`;
        }

        const etiqueta = nodo.nodeName.toLowerCase();
        if (interior.trim() === '') return '';
        return `<${etiqueta}>${interior}</${etiqueta}>`;
    };

    return Array.from(doc.body.childNodes).map(recorrer).join('').trim();
};

/** Nombre de archivo legible a partir de su URL. */
const nombreArchivo = (url) => {
    try {
        const limpia = String(url).split('?')[0];
        return decodeURIComponent(limpia.substring(limpia.lastIndexOf('/') + 1)) || limpia;
    } catch {
        return String(url);
    }
};

/**
 * @param ticket     ticket completo tal cual llega del backend
 * @param etiquetas  { estado, prioridad, departamento, tecnico } ya traducidas a texto
 * @param archivos   array de URLs adjuntas
 * @param enlace     URL del ticket en la aplicación
 * @returns { texto, html } listos para el portapapeles
 */
export const construirCopiaTicket = ({ ticket, etiquetas = {}, archivos = [], enlace = '' }) => {
    const nombreCliente = [limpiar(ticket.nombre), limpiar(ticket.apellidos)].filter(Boolean).join(' ');
    const descripcionTexto = htmlATextoPlano(ticket.descripcion);
    const descripcionHtml = sanearFragmento(ticket.descripcion);
    const creado = fechaLarga(ticket.createdAt);
    const actualizado = fechaLarga(ticket.updatedAt);

    // Pares clave/valor: se omiten los vacíos en vez de imprimir "—" a pares,
    // que llena el pegado de ruido.
    const gestion = [
        ['Estado', etiquetas.estado],
        ['Prioridad', etiquetas.prioridad],
        ['Departamento', etiquetas.departamento],
        ['Técnico asignado', etiquetas.tecnico],
        ['Creado', creado],
        ['Última actualización', actualizado && actualizado !== creado ? actualizado : ''],
    ].filter(([, valor]) => limpiar(valor) !== '');

    const cliente = [
        ['Nombre', nombreCliente],
        ['Email', limpiar(ticket.email)],
        ['Teléfono', limpiar(ticket.telefono)],
        ['Webs', limpiar(ticket.webs_del_cliente)],
    ].filter(([, valor]) => valor !== '');

    // ── Texto plano ──────────────────────────────────────────────────────
    const bloques = [];

    bloques.push([
        `Ticket #${limpiar(ticket.identificador) || GUION}`,
        `Asunto: ${limpiar(ticket.asunto) || 'Sin asunto'}`,
    ].join('\n'));

    if (gestion.length) {
        bloques.push(gestion.map(([clave, valor]) => `${clave}: ${valor}`).join('\n'));
    }

    if (cliente.length) {
        bloques.push(['Cliente', ...cliente.map(([clave, valor]) => `- ${clave}: ${valor}`)].join('\n'));
    }

    bloques.push(['Descripción', descripcionTexto || 'No especificada'].join('\n'));

    if (archivos.length) {
        bloques.push([
            `Archivos adjuntos (${archivos.length})`,
            ...archivos.map((url) => `- ${nombreArchivo(url)}: ${url}`),
        ].join('\n'));
    }

    if (enlace) bloques.push(`Ver el ticket: ${enlace}`);

    const texto = bloques.join('\n\n');

    // ── HTML ─────────────────────────────────────────────────────────────
    // Medidas en pt y una sola familia tipográfica: así Word, Outlook y Docs
    // lo pegan sin reescalar ni inventar fuentes.
    const base = 'font-family:Arial,Helvetica,sans-serif; font-size:10.5pt; color:#1e272e; line-height:1.45;';
    const titulo = 'margin:0 0 4pt; font-size:13pt; font-weight:bold; color:#1e272e;';
    const seccion = 'margin:14pt 0 4pt; font-size:10pt; font-weight:bold; text-transform:uppercase; letter-spacing:0.4pt; color:#6b7280;';
    const celdaClave = 'padding:2pt 12pt 2pt 0; vertical-align:top; white-space:nowrap; color:#6b7280;';
    const celdaValor = 'padding:2pt 0; vertical-align:top;';

    const tabla = (filas) => `<table cellpadding="0" cellspacing="0" style="border-collapse:collapse; ${base}">${
        filas.map(([clave, valor]) => `<tr><td style="${celdaClave}">${escaparHtml(clave)}</td><td style="${celdaValor}">${valor}</td></tr>`).join('')
    }</table>`;

    const valorCliente = (clave, valor) => {
        if (clave === 'Email') return `<a href="mailto:${escaparHtml(valor)}" style="color:#2c41a3;">${escaparHtml(valor)}</a>`;
        if (clave === 'Teléfono') return `<a href="tel:${escaparHtml(valor.replace(/\s+/g, ''))}" style="color:#2c41a3;">${escaparHtml(valor)}</a>`;
        return escaparHtml(valor);
    };

    const partesHtml = [];

    partesHtml.push(`<p style="${titulo}">Ticket #${escaparHtml(limpiar(ticket.identificador) || GUION)}</p>`);
    partesHtml.push(`<p style="margin:0; ${base} font-weight:bold;">${escaparHtml(limpiar(ticket.asunto) || 'Sin asunto')}</p>`);

    if (gestion.length) {
        partesHtml.push(`<p style="${seccion}">Gestión</p>`);
        partesHtml.push(tabla(gestion.map(([clave, valor]) => [clave, escaparHtml(valor)])));
    }

    if (cliente.length) {
        partesHtml.push(`<p style="${seccion}">Cliente</p>`);
        partesHtml.push(tabla(cliente.map(([clave, valor]) => [clave, valorCliente(clave, valor)])));
    }

    partesHtml.push(`<p style="${seccion}">Descripción</p>`);
    partesHtml.push(`<div style="${base}">${descripcionHtml || '<p>No especificada</p>'}</div>`);

    if (archivos.length) {
        partesHtml.push(`<p style="${seccion}">Archivos adjuntos (${archivos.length})</p>`);
        partesHtml.push(`<ul style="${base} margin:0; padding-left:16pt;">${
            archivos.map((url) => `<li><a href="${escaparHtml(url)}" style="color:#2c41a3;">${escaparHtml(nombreArchivo(url))}</a></li>`).join('')
        }</ul>`);
    }

    if (enlace) {
        partesHtml.push(`<p style="margin:14pt 0 0; ${base}"><a href="${escaparHtml(enlace)}" style="color:#2c41a3;">Ver el ticket en Soporte Adhoc</a></p>`);
    }

    const html = `<div style="${base}">${partesHtml.join('')}</div>`;

    return { texto, html };
};
